import React from "react";
import { ChevronDown } from "lucide-react";

export interface GroupedSectionHeaderProps {
  label: string;
  count?: number;
  isCollapsed?: boolean;
  onToggle?: () => void;
  /**
   * Accessible label for toggle button
   */
  toggleAriaLabel?: string;
  /**
   * Control visibility of count badge (default: true when count is provided)
   */
  showCountBadge?: boolean;
  /**
   * Control visibility of chevron toggle button (default: true when onToggle provided)
   */
  showToggle?: boolean;
}

/**
 * Small header used for grouped lists (e.g. email groups: Today, This week, By month).
 * Shows a pill with label (+ optional count), a divider line, and an optional chevron toggle.
 */
const GroupedSectionHeader: React.FC<GroupedSectionHeaderProps> = ({
  label,
  count,
  isCollapsed = false,
  onToggle,
  toggleAriaLabel,
  showCountBadge,
  showToggle,
}) => {
  const shouldShowCount = typeof count === "number" && (showCountBadge ?? true);
  const shouldShowToggle = (showToggle ?? !!onToggle) && !!onToggle;

  return (
    <div className="flex items-center gap-2 w-full">
      {/* Label + optional count pill */}
      <div className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-[3px] py-[1px]">
        <span className="text-[10px] font-medium text-gray-800 leading-4 px-[2.5px] py-0">
          {label}
        </span>
        {shouldShowCount && (
          <span className="inline-flex items-center justify-center rounded-full bg-gray-500 text-white text-[8px] font-medium leading-[14px] px-1 py-0 min-w-[16px]">
            {count}
          </span>
        )}
      </div>

      {/* Divider line */}
      <div className="flex-1 h-px bg-gray-300" />

      {/* Toggle chevron (optional) */}
      {shouldShowToggle && (
        <button
          type="button"
          onClick={onToggle}
          className="w-4 h-4 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
          aria-label={toggleAriaLabel}
        >
          <ChevronDown
            className={`w-4 h-4 transition-transform ${isCollapsed ? "" : "rotate-180"}`}
          />
        </button>
      )}
    </div>
  );
};

export default GroupedSectionHeader;
